import { Form, Input, InputNumber, Popconfirm, Select, Space, Table } from 'antd'
import React, { useState, useRef, useMemo } from 'react'
import HideOnMobile from '../../../../components/generic/hideOnMobile'
import ReviewPayments from './reviewPayments'
import SelectPaymentMethod from '../../../../components/formItems/select/selectPaymentMethod'
import { errorMessage } from '../../../../helpers/errorMessageFunc'
import InitializePaymentsFromCSV from "../../../../components/specific/payments/initializePaymentsFromCSV";
import GenericDropdown from '../../../../components/generic/genericDropdown';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, Stack } from '@mui/material';
import HeaderBox from '../../../../components/generic/arpariBoxes/headerBox'

const { TextArea } = Input;
const { Option } = Select;

const ExternalPayments = (props) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [dataSource, setDataSource] = useState([])
    const [displayData, setDisplayData] = useState([])
    const [formData, setFormData] = useState({})

    const formRef = useRef(null)
    const sortedRecipients = useMemo(() => {
        return [...props.recipients].sort((a, b) => {
            if (!a.name) return 1;
            if (!b.name) return -1;
            return a.name?.localeCompare(b.name, undefined, { sensitivity: 'base' }) || 0;
        });
    }, [props.recipients]);


    const accountHolderRender = (name, nickname) => {
        if (name === nickname || nickname === undefined || nickname === '') {
            return name
        }
        return `${name} (${nickname})`
    }

    const handleChange = (input) => (e, selectedOption) => {
        let value = undefined;
        if (Array.isArray(e)) {
            value = e.slice(-1)[0];
        } else if ((typeof e === "string") | (typeof e === "number")) {
            value = e;
        } else if (e.target !== undefined) {
            value = e.target.value;
        } else {
            value = e.format("YYYY-MM-DD");
        }

        setFormData((prevState) => {
            const newState = { ...prevState }
            newState[input] = value
            return newState
        })
    };

    const accountDescriptor = (acct) => {
        return `${acct.beneficiary.name} / ${acct.account.account_description} / ${accountBalance(acct)}`
    }

    const accountBalance = (acct) => {
        return acct.account.available_balance
    }


    const createNewRow = (newCurrentIndex, initialSendingAccount, initialRecipient, initialDescription, initialAddenda, initialMethod, initialAmount) => {
        const newRow = {
            key: newCurrentIndex,
            from:
                <Form.Item
                    label="Sending Account"
                    name={"sendingAccount" + newCurrentIndex}
                    initialValue={initialSendingAccount || ""}
                >
                    <Select
                        placeholder="Select a bank account"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        dropdownMatchSelectWidth={false}
                        defaultValue={initialSendingAccount || ""}
                        onChange={handleChange("sendingAccount" + newCurrentIndex)}
                    >
                        {props.internalAccounts.map((account) => (
                            <Option key={account.id} value={account.id}>
                                {accountDescriptor(account)}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>,
            to: <Form.Item
                label="Recipient"
                name={"recipient" + newCurrentIndex}
                initialValue={initialRecipient || ""}
                rules={[
                    {
                        required: true,
                        message: "Please select a recipient.",
                    },
                ]}
            >
                <Select
                    onChange={handleChange("recipient" + newCurrentIndex)}
                    allowClear
                    showSearch
                    dropdownMatchSelectWidth={false}
                    filterOption={(input, option) => {
                        const recipient = sortedRecipients.find(r => r.id === option.value);
                        if (!recipient) return false;

                        const name = recipient?.name?.toLowerCase() || "";
                        const inputLower = input.toLowerCase();

                        return name.startsWith(inputLower);
                    }}
                >
                    {sortedRecipients.length !== 0 ? (
                        sortedRecipients.map((recipient) => (
                            <Option key={recipient.id} value={recipient.id}>
                                {accountHolderRender(recipient.name, recipient.nickname)}
                            </Option>
                        ))
                    ) : (
                        <Option value="">
                            Please save a recipient first.
                        </Option>
                    )}
                </Select>
            </Form.Item>,
            method: (
                <SelectPaymentMethod
                    label="Method"
                    name={"method" + newCurrentIndex}
                    onChange={handleChange("method" + newCurrentIndex)}
                    initialValue={initialMethod || ""}
                />
            ),
            description: (
                <Form.Item
                    name={"description" + newCurrentIndex}
                    onChange={handleChange('description' + newCurrentIndex)}
                    initialValue={initialDescription || ""}
                    rules={[
                        {
                            required: true,
                            message: "Please input a description.",
                        },
                    ]}
                >
                    <TextArea showCount maxLength={50} initialValue={initialDescription || ""} />
                </Form.Item>
            ),
            addenda: (
                <Form.Item
                    label="Addenda"
                    name={'addenda' + newCurrentIndex}
                    onChange={handleChange('addenda' + newCurrentIndex)}
                    initialValue={initialAddenda || ""}
                >
                    <TextArea
                        showCount
                        maxLength={80}
                        initialValue={initialAddenda || ""}
                        placeholder={"Optional"}
                    />
                </Form.Item>
            ),
            amount: (
                <Form.Item
                    label="Amount"
                    initialValue={initialAmount || ""}
                    name={"amount" + newCurrentIndex}
                    rules={[
                        {
                            required: true,
                            message: "Please input an amount.",
                        },
                    ]}
                >
                    <InputNumber
                        placeholder="$0.00"
                        formatter={(value) =>
                            `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        onChange={handleChange("amount" + newCurrentIndex)}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                        style={{ width: "100%" }}
                        initialValue={initialAmount || ""}
                    />
                </Form.Item>
            ),
        };

        return [newRow, newCurrentIndex];
    };

    const addNewRow = (initialSendingAccount = undefined, initialRecipient = undefined, initialDescription = undefined, initialAddenda = undefined, initialMethod = undefined, initialAmount = undefined) => {
        return new Promise((resolve) => {
            setCurrentIndex((prevState) => {
                resolve(prevState + 1);
                return prevState + 1;
            });
        }).then((newCurrentIndex) => {
            let res = createNewRow(newCurrentIndex, initialSendingAccount, initialRecipient, initialDescription, initialAddenda, initialMethod, initialAmount);
            setDataSource((prevState) => {
                return [...prevState, res[0]];
            });
        });
    };

    const onFormSubmit = (values) => {
        let data = { ...formData }
        data["recipients"] = props.recipients;
        let displayData = [];
        let totalamount = 0;
        for (let i = 1; i <= currentIndex + 1; i++) {
            if (values["amount" + i] !== undefined) {
                if (values["amount" + i] <= 0) {
                    errorMessage("Please ensure all amounts are valid.");
                    return;
                }
                if (data["method" + i] === 'wire' && values["amount" + i] < 1000) {
                    errorMessage("Wire minimum $1,000. Please use ACH.")
                    return;
                }

                const sendingAccountId = data["sendingAccount" + i];
                const recipientId = data["recipient" + i];
                const sendingAccount = props.internalAccounts.find(account => account.id === sendingAccountId);

                const item = {
                    sendingAccount: {
                        id: sendingAccountId,
                        description: sendingAccount.description,
                        balance: sendingAccount.last_current_balance
                    },
                    recipient: {
                        id: recipientId,
                        description: data.recipients.find((recipient) => {
                            return recipient.id === recipientId;
                        }).name
                    },
                    method: data["method" + i],
                    amount: data["amount" + i],
                    description: data["description" + i],
                    addenda: data["addenda" + i],
                };
                displayData.push(item);
                totalamount += parseFloat(data["amount" + i]);
            }
        }

        if (displayData.length === 0) {
            errorMessage("Please add at least one row.");
            return;
        }
        const item = {
            sendingAccount: "",
            recipient: "",
            method: "Total",
            amount: totalamount,
        };
        displayData.push(item);

        setDisplayData(displayData)
        setShowReviewModal(true)

    };

    const deleteRow = (index) => {
        setDataSource((prevState) => {
            const filteredRows = prevState.filter(
                (row) => row.key !== index
            );
            return filteredRows
        })
    };

    const closeReviewModal = () => {
        setShowReviewModal(false)
    };

    const resetPaymentForm = () => {
        setCurrentIndex(0)
        setShowReviewModal(false)
        setDataSource([])

        if (formRef.current) {
            formRef.current.resetFields();
        }
    };

    const handleCSVUpload = (csvData) => {
        const updateAccount = (accountKey, last4, amount, description, addenda, method, currIndex) => {
            setFormData(prevState => ({
                ...prevState,
                [`amount${currIndex}`]: parseFloat(amount.replace(/\$|,/g, '')),
                [`description${currIndex}`]: description,
                [`addenda${currIndex}`]: addenda,
                [`method${currIndex}`]: method,
                [`${accountKey}${currIndex}`]: accountKey === "sendingAccount" ? props.last4ToInternalAccountMap[last4]?.id : last4,
            }));
        };
        let currIndex = currentIndex + 1;
        for (const csvRow of csvData) {
            let { fromLast4, toLast4, description, addenda, method, amount } = csvRow;
            let methodLabel = method;
            let methodValue = method.toLowerCase();
            if (method === "ACH") methodLabel = `${method} (2-3 business days)`;
            if (method === "Wire") methodLabel = `${method} (0-1 business days, min $1,000)`;
            let recipient = props.recipients.find(recipient => recipient.additional_data.account_number_last_4_attribute === toLast4);
            addNewRow(
                accountDescriptor(props.last4ToInternalAccountMap[fromLast4]),
                recipient?.name,
                description,
                addenda,
                methodLabel,
                amount
            );
            updateAccount('sendingAccount', fromLast4, amount, description, addenda, methodValue, currIndex);
            updateAccount('recipient', recipient?.id, amount, description, addenda, methodValue, currIndex);
            currIndex++;
        }
    }

    const defaultColumns = [
        {
            title: "To/From",
            width: "40%",
            render: (record) => {
                return (<div>
                    {record["from"]}
                    {record["to"]}
                </div>)
            }
        },
        {
            title: "Instructions",
            width: "55%",
            render: (record) => {
                // Determine the maxLength based on the method
                const method = formData[record.method.props.name];
                const maxLength = method === "wire" ? 50 : 10;
                return (
                    <div>
                        {record["method"]}
                        <Form.Item
                            label="Description"
                            name={record.description.props.name}
                            onChange={record.description.props.onChange}
                            initialValue={record.description.props.initialValue}
                            rules={[
                                {
                                    required: true,
                                    message: "Please input a description",
                                },
                            ]}
                        >
                            <TextArea
                                showCount
                                maxLength={maxLength}
                                initialValue={record.description.props.initialValue}
                                placeholder={"Description required as will appear on statements"}
                            />
                        </Form.Item>
                        {method === "ach" && (<>
                            {record["addenda"]}
                        </>
                        )}
                        {record["amount"]}
                    </div>
                )
            }
        },
        {
            // title: "Generate Code",
            width: "5%",
            dataIndex: "generate",
            render: (_, record) => {
                const currentIndex = record.key;
                return (
                    <Form.Item>
                        <GenericDropdown dropdownItems={[
                            <Popconfirm
                                title="Sure to cancel?"
                                onConfirm={() => {
                                    deleteRow(currentIndex);
                                }}
                            >
                                <div
                                    style={{
                                        border: "0px",
                                        color: "red",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                    }}
                                >
                                    Delete
                                </div>
                            </Popconfirm>
                        ]} />
                    </Form.Item>
                );
            },
        },
    ];

    return (
        <Stack spacing={1}>
            <HeaderBox
                title="External Payments"
                ignoreDocumentTitle={true}
                actions={
                    <HideOnMobile>
                        <Space>
                            <Button sx={{ fontSize: 14, textTransform: 'none' }} disableElevation fullWidth size="small" type="submit" variant="outlined" color="secondary" onClick={() => addNewRow()}>
                                Add Row
                            </Button>
                            <InitializePaymentsFromCSV
                                paymentType={"external"}
                                onSuccess={handleCSVUpload}
                            />
                        </Space>
                    </HideOnMobile>
                }
            />
            {
                props.loadingRecipients ||
                    props.loadingInternalAccounts ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', width: '100%' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <>
                        <Form ref={formRef} onFinish={onFormSubmit}
                            labelAlign="right"
                            labelCol={{ "span": 4 }}
                            labelWrap={true}
                        >
                            <Table
                                bordered
                                dataSource={dataSource}
                                columns={defaultColumns}
                                pagination={false}
                                tableLayout="fixed"
                            />

                            <Form.Item>
                                <HideOnMobile>
                                    <Button
                                        htmlType="submit"
                                        style={{ marginTop: "18px" }}
                                        sx={{ fontSize: 14, textTransform: 'none', width: '100px' }}
                                        disableElevation fullWidth
                                        size="small"
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                    >
                                        Submit
                                    </Button>
                                </HideOnMobile>
                            </Form.Item>
                        </Form>
                        {showReviewModal && (
                            <ReviewPayments
                                displayData={displayData}
                                show={showReviewModal}
                                closeReviewModal={closeReviewModal}
                                paymentType="external"
                                resetPaymentForm={resetPaymentForm}
                                updateReceiptsTable={props.updateReceiptsTable}
                                deleteRow={deleteRow}
                            />
                        )}
                    </>
                )
            }
        </Stack>
        // <TitledSection
        //     title="External Payments"
        //     titleExtra={}
        //     body={}
        // />
    )
}

export default ExternalPayments



